import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EntitlementsContext = createContext(null);

// Foundation layer: pages call `can(feature)` to gate UI. While enforcement is
// OFF on the backend, `access` is all-true so nothing is locked for testers.
export function EntitlementsProvider({ children }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    if (!user) { setBilling(null); setLoading(false); return; }
    setLoading(true);
    api.get("/billing/me")
      .then((r) => setBilling(r.data))
      .catch(() => setBilling(null))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const can = useCallback((feature) => {
    if (!billing) return true; // fail-open until loaded (no accidental lockout)
    if (feature in (billing.access || {})) return !!billing.access[feature];
    return true;
  }, [billing]);

  const value = {
    loading,
    plan: billing?.plan || null,
    planLabel: billing?.plan_label || "",
    tier: billing?.tier || "free",
    isPro: !!billing?.is_pro,
    entitlements: billing?.entitlements || {},
    enforcementEnabled: !!billing?.enforcement_enabled,
    paymentsEnabled: !!billing?.payments_enabled,
    can,
    refresh,
  };
  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export const useEntitlements = () => useContext(EntitlementsContext) || {
  loading: false, plan: null, isPro: false, entitlements: {}, can: () => true,
  enforcementEnabled: false, paymentsEnabled: false, refresh: () => {},
};
